import { motion } from 'motion/react';
import { Check, X, Sparkles, Zap, ArrowRight, RotateCcw, Loader2 } from 'lucide-react';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { WaitlistModal } from '../components/WaitlistModal';
import { AuthModal } from '../components/AuthModal';
import { SubscriptionPreviewModal } from '../components/SubscriptionPreviewModal';
import { useMaintenanceCTA } from '../hooks/useMaintenanceCTA';
import { useAuth } from '../context/AuthContext';
import { API_ENDPOINTS } from '@/config/api';
import { trackPlanSelected, trackBillingToggle, trackCheckoutStart } from '@/lib/analytics';
import React from 'react';

/** Default UI metadata per plan (CTA, highlighted, badges). Features come only from API. */
const PLAN_UI_DEFAULTS: Record<string, { cta: string; highlighted: boolean; mostPopular?: boolean; isStarter?: boolean; isProPlus?: boolean; isUltimate?: boolean }> = {
  free: { cta: 'Get Started Free', highlighted: false },
  starter: { cta: 'Get Starter', highlighted: false, isStarter: true },
  pro: { cta: 'Get Pro', highlighted: true, mostPopular: true },
  pro_plus: { cta: 'Get Pro+', highlighted: false, isProPlus: true },
  ultimate: { cta: 'Get Ultimate', highlighted: false, isUltimate: true },
  enterprise: { cta: 'Contact Sales', highlighted: false },
};

const comparisonFeatures = [
  {
    category: 'Core Features',
    items: [
      { name: 'Generation Credits per Month', key: 'generations' },
      { name: 'Extra Generations', key: 'overageRate' },
      { name: 'AI Model', key: 'model' },
      { name: 'Draft variants', key: 'draftVariants' },
      { name: 'Response strategies', key: 'responseStrategies' },
      { name: 'Tone options', key: 'tones' }
    ]
  },
  {
    category: 'Advanced Features',
    items: [
      { name: 'User style matching', key: 'styleMatch' },
      { name: 'Specialized role packages', key: 'rolePackages' },
      { name: 'Early access to new features', key: 'earlyAccess' },
      { name: 'Exclusive community access', key: 'communityAccess' }
    ]
  },
  {
    category: 'Integrations',
    items: [
      { name: 'Gmail integration', key: 'gmailIntegration' },
      { name: 'LinkedIn integration', key: 'linkedinIntegration' }
    ]
  },
  {
    category: 'Support & Analytics',
    items: [
      { name: 'Analytics', key: 'analytics' },
      { name: 'Support', key: 'support' }
    ]
  }
];

type PlanFromApi = {
  name: string;
  displayName?: string;
  description?: string;
  priceMonthlyUsd: number | null;
  priceYearlyUsd: number | null;
  maxGenerationsPerMonth?: number;
  maxVariants?: number;
  maxGoals?: number;
  overageRate: number | null;
  /** Features for pricing comparison table (only from API). */
  features?: Record<string, unknown>;
  /** role_packages: 0 = none, 1 = one included (user selects), -1 = all. Drives dropdown. */
  rolePackages?: number | null;
  /** Can purchase additional packages (add-ons); for account management later. */
  canPurchasePackages?: boolean;
};

type PackageOption = { id: string; name: string };

export function PricingPage() {
  const [isAnnual, setIsAnnual] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [selectedPlanForComparison, setSelectedPlanForComparison] = useState(2);
  const [apiPlans, setApiPlans] = useState<PlanFromApi[] | null>(null);
  const [plansLoadState, setPlansLoadState] = useState<'loading' | 'success' | 'error'>('loading');
  const [packagesForCheckout, setPackagesForCheckout] = useState<PackageOption[]>([]);
  const [selectedIncludedPackageByPlan, setSelectedIncludedPackageByPlan] = useState<Record<string, string>>({});
  const [subscriptionExpiration, setSubscriptionExpiration] = useState<{ cancel_at_period_end: boolean; expires_at: string | null } | null>(null);
  const [reactivateLoading, setReactivateLoading] = useState(false);
  const [planChangePreview, setPlanChangePreview] = useState<{
    isOpen: boolean;
    isLoading: boolean;
    error: string | null;
    preview: {
      type: 'plan_change';
      current_plan: string;
      new_plan: string;
      new_plan_id: string;
      billing_interval: string;
      change_type: 'upgrade' | 'downgrade';
      new_plan_price: number;
      proration_amount: number;
      amount_due: number;
      credit_amount: number;
      charge_amount: number;
      next_billing_date: string;
      currency: string;
    } | null;
    licenseKey: string | null;
    includedPackageId: string | null;
  }>({ isOpen: false, isLoading: false, error: null, preview: null, licenseKey: null, includedPackageId: null });
  const { isWaitlistOpen, setIsWaitlistOpen, handleCTA, isMaintenanceMode } = useMaintenanceCTA();
  const { isAuthenticated, user, license, updateLicense, refreshLicense } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Refresh license when authenticated
  useEffect(() => {
    if (isAuthenticated) refreshLicense();
  }, [isAuthenticated, refreshLicense]);

  // Fetch expiration from usage-info (keyed by license_key) so "Valid through" shows on Pricing
  useEffect(() => {
    if (!isAuthenticated || !license?.license_key) {
      setSubscriptionExpiration(null);
      return;
    }
    let cancelled = false;
    fetch(API_ENDPOINTS.USAGE_INFO, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ licenseKey: license.license_key }),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled || !data?.valid) return;
        const scheduled = data.cancel_at_period_end === true || String(data.cancel_at_period_end || '').toLowerCase() === 'true';
        setSubscriptionExpiration({
          cancel_at_period_end: scheduled,
          expires_at: data.expires_at ?? null,
        });
      })
      .catch(() => {
        if (!cancelled) setSubscriptionExpiration(null);
      });
    return () => { cancelled = true; };
  }, [isAuthenticated, license?.license_key]);

  const fetchSubscriptionExpiration = () => {
    if (!license?.license_key) return;
    fetch(API_ENDPOINTS.USAGE_INFO, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ licenseKey: license.license_key }),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data?.valid) return;
        const scheduled = data.cancel_at_period_end === true || String(data.cancel_at_period_end || '').toLowerCase() === 'true';
        setSubscriptionExpiration({
          cancel_at_period_end: scheduled,
          expires_at: data.expires_at ?? null,
        });
      })
      .catch(() => setSubscriptionExpiration(null));
  };

  const handleReactivate = async () => {
    if (!license?.license_key) return;
    setReactivateLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.SUBSCRIBE_REACTIVATE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ licenseKey: license.license_key }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        return;
      }
      refreshLicense();
      fetchSubscriptionExpiration();
    } finally {
      setReactivateLoading(false);
    }
  };

  // After LinkedIn OAuth callback: user lands on /pricing?checkout=Pro — run checkout once
  const handledCheckoutRef = React.useRef<string | null>(null);
  useEffect(() => {
    const plan = searchParams.get('checkout');
    if (!plan || !isAuthenticated || !user || handledCheckoutRef.current === plan) return;
    handledCheckoutRef.current = plan;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('checkout');
      return next;
    }, { replace: true });
    proceedToCheckout(plan);
  }, [isAuthenticated, user, searchParams]);

  useEffect(() => {
    let cancelled = false;
    setPlansLoadState('loading');
    fetch(API_ENDPOINTS.PLANS_LIST)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        if (Array.isArray(data?.plans) && data.plans.length > 0) {
          setApiPlans(data.plans);
          setPlansLoadState('success');
        } else {
          setPlansLoadState('error');
        }
      })
      .catch(() => {
        if (!cancelled) setPlansLoadState('error');
      });
    return () => { cancelled = true; };
  }, []);

  // Packages for included-package selector (plans with can_purchase_packages, e.g. Pro)
  useEffect(() => {
    let cancelled = false;
    fetch(API_ENDPOINTS.PACKAGES_DEFINITIONS)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data?.packages) ? data.packages : [];
        const withId = list
          .filter((p: { id?: string }) => p.id)
          .map((p: { id: string; name: string; base?: boolean }) => ({ id: p.id, name: p.name || p.id, base: p.base }));
        const nonBase = withId.filter((p) => !p.base);
        setPackagesForCheckout((nonBase.length ? nonBase : withId).map((p) => ({ id: p.id, name: p.name })));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const plans = useMemo(() => {
    if (!apiPlans?.length) return [];
    const order = ['free', 'starter', 'pro', 'pro_plus', 'ultimate'];
    return order
      .map((key) => {
        const api = apiPlans.find((p) => p.name.toLowerCase() === key);
        const ui = PLAN_UI_DEFAULTS[key] || { cta: `Get ${key}`, highlighted: false };
        const name = api?.name ?? key;
        const displayName = api?.displayName ?? api?.name ?? key;
        const priceMonthly = Math.round(api?.priceMonthlyUsd ?? 0);
        const priceYearly = api?.priceYearlyUsd ?? 0;
        const generations = api?.maxGenerationsPerMonth != null
          ? (api.maxGenerationsPerMonth >= 1000 ? api.maxGenerationsPerMonth.toLocaleString() : String(api.maxGenerationsPerMonth))
          : '0';
        const rawDescription = api?.description ?? '';
        const description = rawDescription.replace(/(\d+(?:,\d+)*)(\s*generations?)/gi, `${generations}$2`);
        const overageRate = api?.overageRate != null ? `$${api.overageRate.toFixed(2)}` : null;
        return {
          planKey: key,
          name,
          displayName,
          priceMonthly,
          priceAnnual: priceYearly > 0 ? Math.round(priceYearly / 12) : 0,
          annualTotal: Math.round(priceYearly),
          period: key === 'free' ? 'forever' as const : '/mo' as const,
          description,
          generations,
          overageRate,
          overageSubtext: key === 'free' ? 'Perfect for trying xReplAI' : null,
          ...ui,
          features: api?.features ?? {},
          rolePackages: api?.rolePackages != null ? Number(api.rolePackages) : null,
          canPurchasePackages: Boolean(api?.canPurchasePackages),
        };
      })
  }, [apiPlans]);

  const userPlan = license?.plan?.toLowerCase() || null;

  const getPlanRank = (planName: string): number => {
    const key = (planName || '').toLowerCase();
    const ranks: Record<string, number> = {
      free: 0,
      starter: 1,
      pro: 2,
      pro_plus: 3,
      ultimate: 4,
      enterprise: 5,
    };
    return ranks[key] ?? -1;
  };

  // Compare using canonical keys (planKey / license.plan from API are e.g. 'free', 'pro_plus')
  const isCurrentPlan = (planKeyOrName: string): boolean => {
    if (!userPlan) return false;
    const normalized = (planKeyOrName || '').toLowerCase().replace(/\s+/g, '_');
    const userNormalized = userPlan.replace(/\s+/g, '_');
    return normalized === userNormalized;
  };

  const isDowngrade = (planName: string): boolean => {
    if (!userPlan) return false;
    return getPlanRank(planName) < getPlanRank(userPlan);
  };

  const getButtonText = (plan: typeof plans[0]): string => {
    if (isCurrentPlan(plan.planKey) && subscriptionExpiration?.cancel_at_period_end) {
      return 'Undo Cancellation'; // Shown as button; rendered in separate branch
    }
    return plan.planKey === 'free' ? 'Get Free Plan' : `Get ${plan.displayName} Plan`;
  };

  const handlePlanClick = (planName: string) => {
    if (isMaintenanceMode) {
      setIsWaitlistOpen(true);
      return;
    }

    if (isCurrentPlan(planName)) {
      navigate('/account');
      return;
    }

    const planObj = plans.find((p) => p.name === planName);
    const planKey = planObj?.planKey;
    const price = isAnnual ? (planObj?.priceAnnual ?? 0) : (planObj?.priceMonthly ?? 0);
    trackPlanSelected(planName, isAnnual ? 'yearly' : 'monthly', price);

    trackPlanSelected(planKey || planName, isAnnual ? 'annual' : 'monthly');

    if (!isAuthenticated) {
      setSelectedPlan(planName);
      // Persist so after LinkedIn OAuth redirect we send user to checkout (callback reads this)
      if (planName !== 'Free') {
        sessionStorage.setItem('post_auth_checkout_plan', planName);
        sessionStorage.setItem('post_auth_billing_interval', isAnnual ? 'yearly' : 'monthly');
        const includedId = planKey ? selectedIncludedPackageByPlan[planKey] || packagesForCheckout[0]?.id || '' : '';
        sessionStorage.setItem('post_auth_checkout_included_package_id', includedId);
      }
      setIsAuthModalOpen(true);
    } else {
      proceedToCheckout(planName);
    }
  };

  const proceedToCheckout = async (planName: string) => {
    // Free plan: OAuth only. User gets free license when they sign in (auth/register).
    if (planName === 'Free') {
      if (!isAuthenticated || !user?.email) {
        setSelectedPlan(planName);
        setIsAuthModalOpen(true);
        return;
      }
      // Already signed in: go to account (license was created at sign-up)
      navigate('/account');
      return;
    }

    // For paid plans
    const billing = isAnnual ? 'yearly' : 'monthly';
    
    // Plan name from API is canonical (e.g. pro_plus); pass through for subscribe API
    const apiPlanName = (planName || '').toLowerCase();

    const planObj = plans.find((p) => p.name === planName);
    const planKey = planObj?.planKey;
    const includedPackageId =
      (planKey ? selectedIncludedPackageByPlan[planKey] : null) ||
      sessionStorage.getItem('post_auth_checkout_included_package_id') ||
      (planObj?.rolePackages === 1 && packagesForCheckout[0]?.id ? packagesForCheckout[0].id : null);

    try {
      // Check if this is a plan change (replace_plan) or new subscription
      const statusRes = await fetch(
        `${API_ENDPOINTS.SUBSCRIBE_PLAN_STATUS}?email=${encodeURIComponent(user?.email ?? '')}&plan=${encodeURIComponent(apiPlanName)}`
      );
      const statusData = statusRes.ok ? await statusRes.json() : null;
      const action = statusData?.action;

      // replace_plan: show preview modal with proration before confirming
      if (action === 'replace_plan') {
        const replaceLicenseKey = statusData?.license_key;
        if (!replaceLicenseKey) {
          throw new Error('Unable to determine current license. Please refresh the page or sign out and sign in again, then try upgrading.');
        }
        
        // Open preview modal and fetch proration preview
        setPlanChangePreview({
          isOpen: true,
          isLoading: true,
          error: null,
          preview: null,
          licenseKey: replaceLicenseKey,
          includedPackageId: includedPackageId || null,
        });

        try {
          const previewRes = await fetch(API_ENDPOINTS.SUBSCRIBE_PREVIEW_PLAN_CHANGE, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              licenseKey: replaceLicenseKey,
              newPlan: apiPlanName,
              billingInterval: billing,
            }),
          });
          const previewData = await previewRes.json();
          
          if (!previewRes.ok || !previewData.success) {
            setPlanChangePreview(prev => ({
              ...prev,
              isLoading: false,
              error: previewData.error || 'Failed to calculate proration preview',
            }));
            return;
          }

          setPlanChangePreview(prev => ({
            ...prev,
            isLoading: false,
            preview: { type: 'plan_change', ...previewData.preview },
          }));
        } catch (previewError) {
          setPlanChangePreview(prev => ({
            ...prev,
            isLoading: false,
            error: 'Failed to load preview. Please try again.',
          }));
        }
        return;
      }

      // New user or upgrade_free: redirect to embedded checkout for new subscription
      trackCheckoutStart(apiPlanName, billing, 'new_subscription');
      const couponCode = searchParams.get('coupon');
      const checkoutParams = new URLSearchParams({
        plan: apiPlanName,
        billing,
        type: 'new_subscription',
        ...(includedPackageId ? { included_package_id: includedPackageId } : {}),
        ...(couponCode ? { coupon: couponCode } : {}),
      });
      navigate(`/checkout?${checkoutParams.toString()}`);
    } catch (error) {
      console.error('Checkout error:', error);
      const msg = (error instanceof Error ? error.message : String(error)).trim();
      alert(msg || 'Something went wrong. Please try again or refresh the page.');
    }
  };

  const handleConfirmPlanChange = async () => {
    if (!planChangePreview.preview || !planChangePreview.licenseKey) return;

    try {
      const res = await fetch(API_ENDPOINTS.SUBSCRIBE_UPDATE_PLAN_DIRECT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          licenseKey: planChangePreview.licenseKey,
          newPlan: planChangePreview.preview.new_plan,
          billingInterval: planChangePreview.preview.billing_interval,
          ...(planChangePreview.includedPackageId ? { included_package_id: planChangePreview.includedPackageId } : {}),
        }),
      });
      const data = await res.json();

      if (!res.ok || !data.success) {
        setPlanChangePreview(prev => ({
          ...prev,
          error: data.error || 'Failed to update plan',
        }));
        return;
      }

      // Success - close modal and refresh
      setPlanChangePreview({ isOpen: false, isLoading: false, error: null, preview: null, licenseKey: null, includedPackageId: null });
      await refreshLicense();
      navigate('/account');
    } catch (error) {
      setPlanChangePreview(prev => ({
        ...prev,
        error: 'Failed to update plan. Please try again.',
      }));
    }
  };

  const handleAuthSuccess = () => {
    // After successful auth, proceed to checkout with the selected plan
    if (selectedPlan) {
      proceedToCheckout(selectedPlan);
      setSelectedPlan(null);
    }
  };

  const renderFeatureValue = (plan: typeof plans[0], value: unknown) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Check size={20} className="text-[#60A5FA] mx-auto" />
      ) : (
        <X size={20} className="text-gray-300 dark:text-gray-600 mx-auto" />
      );
    }
    if (value === null || value === undefined) return <span className="text-sm text-gray-500">—</span>;
    return <span className="text-sm text-gray-700 dark:text-gray-300">{String(value)}</span>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1E3A8A] via-[#0F2847] to-[#1E3A8A]">
      <Header />

      <main className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {plansLoadState === 'loading' && (
            <motion.div
              className="text-center py-24"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <p className="text-xl text-white/80">Loading plans…</p>
            </motion.div>
          )}

          {plansLoadState === 'error' && (
            <motion.div
              className="text-center py-24 max-w-xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
            >
              <h2 className="text-2xl font-semibold text-white mb-4">Pricing temporarily unavailable</h2>
              <p className="text-white/80 mb-6">
                This page is under maintenance at the moment. Please try again later.
              </p>
            </motion.div>
          )}

          {plansLoadState === 'success' && plans.length > 0 && (
            <>
          {/* Founding Member Banner */}
          <motion.div
            className="mb-8 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="relative bg-gradient-to-r from-amber-500/20 via-yellow-500/20 to-amber-500/20 border border-amber-400/40 rounded-xl px-6 py-4 text-center backdrop-blur-sm">
              <div className="flex items-center justify-center gap-2 mb-1">
                <Sparkles size={18} className="text-amber-400" />
                <span className="text-amber-300 font-bold text-sm uppercase tracking-wider">Founding Member Offer</span>
                <Sparkles size={18} className="text-amber-400" />
              </div>
              <p className="text-white/90 text-sm">
                Get <strong className="text-amber-300">20% off</strong> any annual plan with code <code className="bg-white/10 px-2 py-0.5 rounded text-amber-300 font-mono font-bold">FOUNDING20</code> at checkout. Limited to first 100 subscribers.
              </p>
            </div>
          </motion.div>

          {/* Header */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6">
              Compare Plans
            </h1>
            <p className="text-xl text-white/80 max-w-3xl mx-auto mb-8">
              Choose the perfect plan for your needs. All plans include core features with flexible Pay-As-You-Go pricing.
            </p>

            {/* Billing Toggle */}
            <div className="flex items-center justify-center gap-4">
              <span className={`text-lg font-medium transition-colors ${!isAnnual ? 'text-white' : 'text-white/50'}`}>
                Monthly
              </span>
              <button
                onClick={() => { setIsAnnual(!isAnnual); trackBillingToggle(!isAnnual); }}
                className={`relative w-16 h-8 rounded-full transition-colors ${isAnnual ? 'bg-[#60A5FA]' : 'bg-white/20'}`}
              >
                <motion.div
                  className="absolute top-1 left-1 w-6 h-6 bg-white rounded-full shadow-md"
                  animate={{ x: isAnnual ? 32 : 0 }}
                  transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                />
              </button>
              <div className="flex items-center gap-2">
                <span className={`text-lg font-medium transition-colors ${isAnnual ? 'text-white' : 'text-white/50'}`}>
                  Annual
                </span>
                <span className="px-2 py-1 bg-green-500 text-white text-xs font-semibold rounded-full">
                  Save up to 30%
                </span>
              </div>
            </div>
          </motion.div>

          {/* Plan Cards Summary */}
          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`relative p-6 rounded-xl border-2 transition-all ${
                  plan.isUltimate
                    ? 'bg-gradient-to-br from-amber-500 via-yellow-500 to-amber-600 border-amber-400'
                    : plan.highlighted
                    ? 'bg-gradient-to-br from-[#1E3A8A] to-[#60A5FA] border-[#60A5FA]'
                    : plan.isProPlus
                    ? 'bg-gradient-to-br from-indigo-600 to-blue-500 border-indigo-400'
                    : plan.isStarter
                    ? 'bg-white/10 backdrop-blur-sm border-white/20'
                    : 'bg-white/5 backdrop-blur-sm border-white/10'
                }`}
              >
                {isCurrentPlan(plan.planKey) && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-green-500 text-white rounded-full flex items-center gap-1 text-xs font-semibold">
                    Your Plan
                  </div>
                )}
                {plan.mostPopular && !isCurrentPlan(plan.planKey) && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-[#60A5FA] text-white rounded-full flex items-center gap-1 text-xs font-semibold">
                    <Sparkles size={12} />
                    Most Popular
                  </div>
                )}

                {plan.isUltimate && !isCurrentPlan(plan.planKey) && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-amber-600 to-yellow-600 text-white rounded-full flex items-center gap-1 text-xs font-semibold">
                    <Sparkles size={12} className="animate-pulse" />
                    Ultimate
                  </div>
                )}

                <h3 className={`text-xl font-bold mb-2 ${plan.isUltimate || plan.highlighted || plan.isProPlus ? 'text-white' : 'text-white'}`}>
                  {plan.displayName}
                </h3>
                <p className={`text-xs mb-4 ${plan.isUltimate || plan.highlighted || plan.isProPlus ? 'text-white/70' : 'text-white/60'}`}>
                  {plan.description}
                </p>
                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className={`text-4xl font-bold ${plan.isUltimate || plan.highlighted || plan.isProPlus ? 'text-white' : 'text-white'}`}>
                      ${isAnnual ? plan.priceAnnual : plan.priceMonthly}
                    </span>
                    <span className={`text-sm ${plan.isUltimate || plan.highlighted || plan.isProPlus ? 'text-white/80' : 'text-white/70'}`}>
                      /{plan.priceMonthly === 0 ? 'forever' : 'mo'}
                    </span>
                  </div>
                                    {isAnnual && plan.annualTotal > 0 && (
                                      <p className={`text-xs mt-1 ${plan.isUltimate || plan.highlighted || plan.isProPlus ? 'text-white/70' : 'text-white/60'}`}>
                                        Billed at ${plan.annualTotal}/year
                                      </p>
                                    )}
                                </div>
                                {isCurrentPlan(plan.planKey) && subscriptionExpiration?.expires_at && (
                                  <p className="text-xs mb-4 px-3 py-2 rounded-lg bg-amber-400/30 text-amber-900 dark:bg-amber-300/20 dark:text-amber-100 border border-amber-500/50 dark:border-amber-400/50 font-medium">
                                    {subscriptionExpiration.cancel_at_period_end
                                      ? `Valid through ${new Date(subscriptionExpiration.expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
                                      : `Renews on ${new Date(subscriptionExpiration.expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`}
                                  </p>
                                )}
                                <div className="flex items-center gap-2 mb-4">
                                  <Zap size={14} className={plan.isUltimate || plan.highlighted || plan.isProPlus ? 'text-white' : 'text-[#60A5FA]'} />
                  <span className={`text-sm font-medium ${plan.isUltimate || plan.highlighted || plan.isProPlus ? 'text-white' : 'text-white'}`}>
                    {plan.generations} generations
                  </span>
                </div>
                {(plan.overageRate != null || plan.overageSubtext) && (
                  <p className={`text-xs mb-4 ${plan.isUltimate || plan.highlighted || plan.isProPlus ? 'text-white/70' : 'text-white/60'}`}>
                    {plan.overageRate ?? plan.overageSubtext}
                  </p>
                )}
                {plan.rolePackages === 1 && packagesForCheckout.length > 0 && (
                  <div className="mb-4">
                    <label className={`block text-xs font-medium mb-1 ${plan.isUltimate || plan.highlighted || plan.isProPlus ? 'text-white/80' : 'text-white/70'}`}>
                      Included package
                    </label>
                    <select
                      value={selectedIncludedPackageByPlan[plan.planKey] || packagesForCheckout[0]?.id || ''}
                      onChange={(e) => setSelectedIncludedPackageByPlan((prev) => ({ ...prev, [plan.planKey]: e.target.value }))}
                      className={`w-full rounded-lg border px-3 py-2 text-sm bg-white/10 border-white/30 text-white focus:ring-2 focus:ring-[#60A5FA] focus:border-[#60A5FA] ${plan.isUltimate || plan.highlighted || plan.isProPlus ? 'text-white' : 'text-white'}`}
                    >
                      {packagesForCheckout.map((pkg) => (
                        <option key={pkg.id} value={pkg.id} className="bg-gray-900 text-white">
                          {pkg.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {isCurrentPlan(plan.planKey) && subscriptionExpiration?.cancel_at_period_end ? (
                  <button
                    type="button"
                    onClick={handleReactivate}
                    disabled={reactivateLoading}
                    className="w-full py-3 rounded-lg font-semibold text-sm transition-all inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white border-2 border-amber-400"
                  >
                    {reactivateLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                    Undo Cancellation
                  </button>
                ) : !isCurrentPlan(plan.planKey) ? (
                  <button
                    onClick={() => handlePlanClick(plan.name)}
                    className={`w-full py-3 rounded-lg font-semibold text-sm transition-all ${
                      isDowngrade(plan.name)
                        ? 'bg-white/10 text-white/60 hover:bg-white/15'
                        : plan.isUltimate
                        ? 'bg-white text-amber-700 hover:bg-white/90'
                        : plan.highlighted
                        ? 'bg-white text-[#1E3A8A] hover:bg-white/90'
                        : plan.isProPlus
                        ? 'bg-white text-indigo-700 hover:bg-white/90'
                        : 'bg-white/20 text-white hover:bg-white/30'
                    }`}
                  >
                    {getButtonText(plan)}
                  </button>
                ) : null}
              </div>
            ))}
          </motion.div>

          {/* Detailed Comparison Table */}
          <motion.div
            className="bg-white dark:bg-[#0F172A] rounded-2xl shadow-2xl overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {/* Mobile Plan Selector */}
            <div className="block lg:hidden p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Select Plan to Compare</h3>
              <div className="grid grid-cols-2 gap-3">
                {plans.map((plan, index) => (
                  <button
                    key={plan.name}
                    onClick={() => setSelectedPlanForComparison(index)}
                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                      selectedPlanForComparison === index
                        ? 'border-[#60A5FA] bg-[#60A5FA]/10'
                        : 'border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        selectedPlanForComparison === index
                          ? 'border-[#60A5FA] bg-[#60A5FA]'
                          : 'border-gray-300 dark:border-gray-600'
                      }`}>
                        {selectedPlanForComparison === index && (
                          <div className="w-2 h-2 bg-white rounded-full" />
                        )}
                      </div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{plan.displayName}</span>
                    </div>
                    <span className="text-xs text-gray-600 dark:text-gray-400">
                      ${isAnnual ? plan.priceAnnual : plan.priceMonthly}/{plan.priceMonthly === 0 ? 'forever' : 'mo'}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Mobile Single Column Feature List */}
            <div className="block lg:hidden">
              {comparisonFeatures.map((category, categoryIndex) => (
                <div key={categoryIndex}>
                  <div className="px-6 py-3 bg-gray-100 dark:bg-[#1E3A8A]/10">
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white">{category.category}</h4>
                  </div>
                  {category.items.map((item, itemIndex) => (
                    <div
                      key={itemIndex}
                      className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between"
                    >
                      <span className="text-sm text-gray-700 dark:text-gray-300">{item.name}</span>
                      <div className="flex-shrink-0">
                        {renderFeatureValue(
                          plans[selectedPlanForComparison],
                          plans[selectedPlanForComparison].features[item.key as keyof typeof plans[0]['features']]
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}
              
              {/* Mobile CTA Button */}
              <div className="p-6">
                {isCurrentPlan(plans[selectedPlanForComparison].planKey) && subscriptionExpiration?.cancel_at_period_end ? (
                  <button
                    type="button"
                    onClick={handleReactivate}
                    disabled={reactivateLoading}
                    className="w-full py-4 font-semibold rounded-lg transition-all inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white"
                  >
                    {reactivateLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                    Undo Cancellation
                  </button>
                ) : !isCurrentPlan(plans[selectedPlanForComparison].planKey) ? (
                  <button
                    onClick={() => handlePlanClick(plans[selectedPlanForComparison].name)}
                    className={`w-full py-4 font-semibold rounded-lg transition-all ${
                      isDowngrade(plans[selectedPlanForComparison].name)
                        ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600'
                        : 'bg-[#60A5FA] text-white hover:bg-[#60A5FA]/90'
                    }`}
                  >
                    {getButtonText(plans[selectedPlanForComparison])}
                  </button>
                ) : null}
                {isCurrentPlan(plans[selectedPlanForComparison].planKey) && subscriptionExpiration?.expires_at && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 text-center">
                    {subscriptionExpiration.cancel_at_period_end
                      ? `Valid through ${new Date(subscriptionExpiration.expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`
                      : `Renews on ${new Date(subscriptionExpiration.expires_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`}
                  </p>
                )}
              </div>
            </div>

            {/* Desktop Comparison Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-[#1E3A8A]/20 border-b border-gray-200 dark:border-gray-700">
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-white sticky left-0 bg-gray-50 dark:bg-[#1E3A8A]/20 z-10">
                      Features
                    </th>
                    {plans.map((plan) => (
                      <th key={plan.name} className="px-6 py-4 text-center text-sm font-semibold text-gray-900 dark:text-white min-w-[140px]">
                        {plan.displayName}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonFeatures.map((category, categoryIndex) => [
                    <tr key={`category-${categoryIndex}`} className="bg-gray-100 dark:bg-[#1E3A8A]/10">
                      <td colSpan={6} className="px-6 py-3 text-sm font-bold text-gray-900 dark:text-white">
                        {category.category}
                      </td>
                    </tr>,
                    ...category.items.map((item, itemIndex) => (
                      <tr
                        key={`${categoryIndex}-${itemIndex}`}
                        className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#1E3A8A]/5 transition-colors"
                      >
                        <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300 sticky left-0 bg-white dark:bg-[#0F172A] z-10">
                          {item.name}
                        </td>
                        {plans.map((plan) => (
                          <td key={plan.name} className="px-6 py-4 text-center">
                            {renderFeatureValue(plan, plan.features[item.key as keyof typeof plan.features])}
                          </td>
                        ))}
                      </tr>
                    ))
                  ])}
                </tbody>
              </table>
            </div>
          </motion.div>

          {/* Footnote */}
          <motion.div
            className="mt-8 text-center max-w-4xl mx-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <p className="text-sm text-white/70">
              <strong>Note:</strong> 1 Generation = one full AI draft set (up to 4 variants based on goal/role + tone). 
              Extra generations are automatically billed monthly via Stripe Metered Billing. You'll receive nudges at 70-80% quota 
              and when projected costs approach the next tier price.
            </p>
          </motion.div>

          {/* Enterprise CTA */}
          <motion.div
            className="mt-16 max-w-5xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <div className="relative p-8 rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 border-2 border-gray-700 shadow-2xl overflow-hidden">
              <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
              </div>
              
              <div className="relative z-10 text-center">
                <h3 className="text-3xl font-bold text-white mb-3">
                  Need Enterprise Solutions?
                </h3>
                <p className="text-lg text-gray-300 mb-6 max-w-3xl mx-auto">
                  Custom pricing for teams (10+ users) with dedicated support, SSO, API access, on-premise deployment, and compliance guarantees. Starting at ~$500+/mo with 50,000+ generations.
                </p>
                <a
                  href="/enterprise"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-[#60A5FA] text-white font-semibold rounded-lg hover:bg-[#60A5FA]/90 transition-all shadow-lg shadow-[#60A5FA]/30"
                >
                  Explore Enterprise Options
                  <ArrowRight size={20} />
                </a>
              </div>
            </div>
          </motion.div>
            </>
          )}
        </div>
      </main>

      <Footer />
      <WaitlistModal isOpen={isWaitlistOpen} onClose={() => setIsWaitlistOpen(false)} />
      <AuthModal isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} onSuccess={handleAuthSuccess} />
      <SubscriptionPreviewModal
        isOpen={planChangePreview.isOpen}
        onClose={() => setPlanChangePreview({ isOpen: false, isLoading: false, error: null, preview: null, licenseKey: null, includedPackageId: null })}
        onConfirm={handleConfirmPlanChange}
        preview={planChangePreview.preview}
        isLoading={planChangePreview.isLoading}
        error={planChangePreview.error}
        modalType="plan_change"
      />
    </div>
  );
}
